import React, { ReactNode, useContext, useMemo } from 'react';
import {
  ConfigContext,
  withLocalization,
  withLocalizationProps,
} from '../../context/Config';
import NewCourseDefaultImg from '../../images/newCourseDetail.svg';
import Text from '../Text';
import { Avatar } from 'antd';
import { randomHex } from '../../utils/helper';
import findLastIndex from 'lodash/findLastIndex';
import withSkeleton from '../../hoc/withSkeleton';
import min from 'lodash/min';
import Popover from '../Popover';
import IconNewCalendar from '../Icons/IconNewCalendar';
import IconNewBook from '../Icons/IconNewBook';
import IconNewUsers from '../Icons/IconNewUsers';
import NewImageCard from '../NewImageCard';
import NewCourseCardSkeleton from './NewCourseCardSkeleton';
import UpcomingSessionCard, { SessionType } from '../UpcomingSessionCard';
import './NewCourseCard.less';

export type NewCourseType = {
  id?: string;
  name?: string | ReactNode;
  description?: string;
  groupTitle?: string;
  startDate?: string;
  endDate?: string;
  teachers?: Array<{
    fullName: string;
    image: string;
  }>;
  level?: string;
  image?: string;
  trainingLinks?: { [key: string]: string };
  practiceLink?: string;
  isFlexible?: boolean;
  price?: string;
  videoComponent?: ReactNode | null;
};

export type NewCourseCardProps = {
  course: NewCourseType;
  contentHeight?: string;
  image?: string;
  actions?: ReactNode;
  studentsCount?: {
    total: number;
    notStopped: number;
    stopped: number;
  };
  lessonsCount?: {
    totalLessons: number;
    participatedLessons: number;
  };
  sessionDate?: any;
  sessionDateISO?: any;
  scheduleViewer?: ReactNode | null;
  addStudentBtn?: ReactNode | null;
  dotMenu?: ReactNode | null;
  userIconFill?: '';
  session?: SessionType;
};

const TNewCourseCard: React.FC<NewCourseCardProps & withLocalizationProps> = ({
  moment,
  course,
  contentHeight = '124px',
  actions,
  lessonsCount,
  studentsCount,
  sessionDate,
  scheduleViewer,
  addStudentBtn,
  dotMenu,
}) => {
  const { translate } = useContext(ConfigContext);
  const randomColorHex = useMemo(randomHex, []);

  const { teachers = [] } = course;

  const nameFirstLetters = (name = '') => {
    const arr = name
      .split(' ')
      .map((s) => s.trim())
      .filter(Boolean);

    while (arr.length > 2) {
      const minVal = min(arr.map((s) => s.length));
      arr.splice(
        findLastIndex(arr, (s) => s.length === minVal),
        1,
      );
    }

    return arr.map((s) => s.charAt(0).toLocaleUpperCase()).join('');
  };

  function showTeacher(teachers) {
    if (!teachers || !teachers.length) {
      return null;
    }

    if (teachers.length === 1) {
      return (
        <div className="t-teachers-info">
          <Avatar
            src={teachers[0]?.image}
            alt={teachers[0]?.fullName}
            icon={nameFirstLetters(teachers[0]?.fullName)}
            style={{ background: randomColorHex, fontSize: 13 }}
          />
          <span className="t-teacher-name">
            <Text level={3} rows={1}>
              {teachers[0]?.fullName}
            </Text>
          </span>
        </div>
      );
    }

    if (teachers.length > 1) {
      return (
        <div className="t-teachers-info">
          <Avatar.Group maxCount={1}>
            <Avatar
              src={teachers[0]?.image || teachers[0]?.fullName}
              alt={teachers[0]?.fullName}
              style={{ background: randomColorHex, fontSize: 13 }}
            />
            {teachers &&
              teachers.map((item, index) => {
                return (
                  <div className="t-teachers-info" key={index}>
                    <Avatar
                      src={item?.image || item?.fullName}
                      alt={item?.fullName}
                      icon={
                        <span className="t-teacher-icon">
                          {nameFirstLetters(item?.fullName)}
                        </span>
                      }
                      style={{ background: randomColorHex, fontSize: 13 }}
                    />
                    <span className="t-teacher-name">
                      <Text level={3} rows={1}>
                        {item?.fullName}
                      </Text>
                    </span>
                  </div>
                );
              })}
          </Avatar.Group>

          <span className="t-teacher-name">
            <Text level={3} rows={1}>
              {translate('Tutors')}
            </Text>
          </span>
        </div>
      );
    }

    return (
      <Text level={3} rows={1}>
        {teachers[0]?.fullName || teachers[0]?.image}
      </Text>
    );
  }

  const teacher = showTeacher(teachers);

  function renderStudentPopoverHeader() {
    return (
      <div className="t-student-wrapper">
        <Text rows={1} level={2} bold>
          {translate('Students')}
        </Text>
        {addStudentBtn}
      </div>
    );
  }

  const renderStudentsData = () => {
    return (
      <div className="t-studentsWrapper">
        <div className="t-studentsData">
          <Text rows={1} level={3}>
            {translate('All Students')}
          </Text>
          <span className="t-dashStyle">-</span>
          <Text rows={1} level={3}>
            {studentsCount?.total || 0}
          </Text>
        </div>
        <div className="t-studentsData">
          <Text rows={1} level={3}>
            {translate('Active')}
          </Text>
          <span className="t-dashStyle">-</span>
          <Text rows={1} level={3}>
            {studentsCount?.notStopped || 0}
          </Text>
        </div>
        <div className="t-studentsData">
          <Text rows={1} level={3}>
            {translate('Stopped')}
          </Text>
          <span className="t-dashStyle">-</span>
          <Text rows={1} level={3}>
            {studentsCount?.stopped || 0}
          </Text>
        </div>
      </div>
    );
  };

  const renderRemainingLessons = () => {
    return (
      <div className="t-remainingLessonsWrapper">
        <div className="t-remainingLessonData">
          <Text rows={1} level={3}>
            {translate('All')}
          </Text>
          <span className="t-dashStyle">-</span>
          <Text rows={1} level={3}>
            {lessonsCount?.totalLessons || 0}
          </Text>
        </div>
        <div className="t-remainingLessonData">
          <Text rows={1} level={3}>
            {translate('Participated')}
          </Text>
          <span className="t-dashStyle">-</span>
          <Text rows={1} level={3}>
            {lessonsCount?.participatedLessons || 0}
          </Text>
        </div>
        <div className="t-remainingLessonData">
          <Text rows={1} level={3}>
            {translate('Left')}
          </Text>
          <span className="t-dashStyle">-</span>
          <Text rows={1} level={3}>
            {lessonsCount?.totalLessons - lessonsCount?.participatedLessons ||
              0}
          </Text>
        </div>
      </div>
    );
  };

  return (
    <NewImageCard
      dotMenu={<div className="t-dot-menu">{dotMenu}</div>}
      image={course?.image || NewCourseDefaultImg}
      videoComponent={course?.videoComponent}
    >
      <div className="t-newActionIcons">
        {/* Students Icon */}
        {!!studentsCount && (
          <div className="t-iconStyle">
            <span className="iconUser">
              <Popover
                trigger="click"
                content={() => renderStudentsData()}
                title={renderStudentPopoverHeader()}
              >
                <IconNewUsers size="md" />
              </Popover>
            </span>
          </div>
        )}
        {/* Lessons Icon */}
        {!!lessonsCount && (
          <div className="t-iconStyle">
            <span className="iconBook">
              <Popover
                trigger="click"
                content={() => renderRemainingLessons()}
                title={
                  <Text rows={1} level={2} bold>
                    {translate('Lessons')}
                  </Text>
                }
              >
                <IconNewBook size="md" />
              </Popover>
            </span>
          </div>
        )}
        {/* Schedule Icon */}
        {!!scheduleViewer && (
          <div className="t-iconStyle">
            <span className="iconCalendar">
              <Popover
                placement="topRight"
                trigger="click"
                content={scheduleViewer}
              >
                <IconNewCalendar size="md" />
              </Popover>
            </span>
          </div>
        )}
      </div>

      <div style={{ height: contentHeight }} className="t-courseCardContent">
        <div className="t-cardContentRow-1">
          <div className="t-courseCardTitle">
            <Text
              title={String(course.groupTitle || '')}
              textColor="dark-100"
              bold
              level={1}
              rows={1}
            >
              {course.groupTitle || ''}
            </Text>
            <Text textColor="dark-50" semiBold level={2} rows={1}>
              {course.name || ''}
            </Text>
          </div>
        </div>

        <div className="t-cardContentRow-2">
          {!!teachers.length && (
            <div className="t-icon-with-text">
              <div className="t-info-text">
                <Text rows={1} level={4}>
                  {teacher}
                </Text>
              </div>
            </div>
          )}
        </div>

        {/* Upcoming section */}
        <div className="t-cardContentRow-3">
          {/* Upcoming Event */}
          {sessionDate && (
            <UpcomingSessionCard
              title={translate('Upcoming')}
              session={{
                date: sessionDate,
              }}
              noSessionText={translate('There is no upcoming session')}
            />
          )}

          {/* Assignment */}
          <div
            className="t-upcoming-second-row"
            style={sessionDate ? { marginTop: 15 } : { marginTop: 'unset' }}
          >
            {sessionDate ? (
              <Text level={3} rows={1}>
                {moment(sessionDate).format('HH:mm ddd, MMM DD')}
              </Text>
            ) : (
              <Text level={3} rows={1}>
                {translate('You don’t have any assignment')}
              </Text>
            )}
            {actions && <div className="actions-wrapper">{actions}</div>}
          </div>
        </div>
      </div>
    </NewImageCard>
  );
};

export default withSkeleton(
  withLocalization(TNewCourseCard),
  NewCourseCardSkeleton,
);
